import React, { useCallback, useEffect, useRef } from 'react';
import { Trash, Edit } from '@withjoy/joykit/icons';
import { Box, ButtonV2, CheckboxV2, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { DragIcon } from '@assets/icons';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useIsTouchDevice } from '@shared/utils/media/useIsTouchDevice';
import { useSortableItemContext } from '@shared/components/SortableList/context/SortableItem.context';
import { useTravelPlacesPricingAndBookingDetailsFragment } from '@shared/hooks/useTravelPlacesPricingAndBookingDetails';
import { useCurrencyContext } from '@shared/utils/currency';
import { useEventLocaleFormatter } from '@shared/core/localization/hooks/useEventLocaleFormatter';
import { HotelTile, TILE_THUMBNAIL_HEIGHT, TILE_BORDER_RADIUS } from '@shared/components/HotelTile';
import { useSoldOutMessage } from '@shared/components/SoldOutTag/useSoldOutMessage';
import { TextEllipsisWrapper } from '@shared/utils/formatting/textEllipsis';
import { SwipeAction } from '@shared/components/SwipeAction';
import { useTourState } from '@shared/components/Tour';
import type { SwipeActionRef } from '@shared/components/SwipeAction';
import type { AccommodationCard } from '../../Accommodations.types';
import { TravelMapBlockType } from '../../Accommodations.types';
import { useAccommodationsContext } from '../../context/useAccommodationsContext';

type SortableHotelTileProps = Readonly<{
  hotel: AccommodationCard;
  isDragActive: boolean;
  containerWidth: number | null;
  isNewlyAdded: boolean;
  onEdit: (hotel: AccommodationCard) => void;
  onRemove: (hotel: AccommodationCard) => void;
  isManageMode: boolean;
  isSelected: boolean;
  onToggleSelection: (id: string) => void;
}>;

// memo is intentional for prop-driven re-renders; context changes (useAccommodationsContext) bypass it
export const SortableHotelTile = React.memo<SortableHotelTileProps>(
  ({ hotel, isDragActive, containerWidth, isNewlyAdded, onEdit, onRemove, isManageMode, isSelected, onToggleSelection }) => {
    const { tq } = useTranslation('accommodationsAdmin');
    const isMobile = useIsMobileScreen();
    const isTouchDevice = useIsTouchDevice();
    const { attributes, listeners, draggableNodeRef } = useSortableItemContext();
    const swipeRef = useRef<SwipeActionRef>(null);
    const tourState = useTourState();
    const isTourActive = tourState?.stateProps.isOpen ?? false;

    useEffect(() => {
      if (isDragActive) {
        swipeRef.current?.close();
      }
    }, [isDragActive]);

    const { accommodationsData } = useAccommodationsContext();
    const { formatDate } = useEventLocaleFormatter();
    const currencyFormatter = useCurrencyContext();

    const hasRoomBlock = hotel.type === TravelMapBlockType.travelMapAccommodationsRoomblock;
    const hasHotel = hotel.type === TravelMapBlockType.travelMapAccommodationsHotel;
    const isCustomProperty = hotel.type === TravelMapBlockType.travelMapAccommodationsCustom;
    const isFavorite = Boolean(hotel.isFavorite);

    const rawPlace = accommodationsData?.accommodationPlacesV2?.find(p => p.id === hotel.id);
    const isHostStayingHere = Boolean(rawPlace?.isHostStayingHere);
    const isDragDisabled = isFavorite;

    // The list renders the hotel's saved photo; catalog photos load on-demand in the edit form, so no
    // catalog fallback here.
    const thumbnailUrl = hotel.photoUrl;

    // Pricing
    const skipPricing = hasRoomBlock || isCustomProperty;
    const {
      price,
      strikeOutPrice,
      percentageOff,
      soldOutUrl,
      loading: pricingLoading,
    } = useTravelPlacesPricingAndBookingDetailsFragment(hotel.id, 'accommodationPlace', skipPricing);
    const roomBlockPrice = hasRoomBlock ? hotel.price : null;
    const roomBlockStrikeout = hasRoomBlock ? hotel.strikeoutPrice : null;
    const roomBlockCurrencyCode = accommodationsData?.travelSettings?.currencyCode ?? undefined;
    const displayPrice = hasRoomBlock ? (roomBlockPrice ? currencyFormatter.formatCurrency(roomBlockPrice, { currency: roomBlockCurrencyCode }) : null) : price;
    const displayStrikeout = hasRoomBlock
      ? roomBlockStrikeout
        ? currencyFormatter.formatCurrency(roomBlockStrikeout, { currency: roomBlockCurrencyCode })
        : null
      : strikeOutPrice;
    const displayPercentOff = hasRoomBlock ? null : percentageOff;

    const cutoffDate = hotel.cutoffDate;
    const formattedCutoffDate = cutoffDate ? (formatDate(cutoffDate, 'MMM dd, yyyy') ?? null) : null;

    const starCount = hotel.starRating ? Math.round(hotel.starRating) : 0;
    const shouldAlignBottom = (hasRoomBlock && formattedCutoffDate) || (hasHotel && !hotel.distances);

    const soldOutMessage = useSoldOutMessage();

    const handleClick = useCallback(() => {
      if (isManageMode) {
        onToggleSelection(hotel.id);
        return;
      }
      onEdit(hotel);
    }, [isManageMode, onToggleSelection, onEdit, hotel]);
    const handleRemoveClick = useCallback(
      (e: React.MouseEvent) => {
        e.stopPropagation();
        onRemove(hotel);
      },
      [onRemove, hotel],
    );
    const handleRowKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onToggleSelection(hotel.id);
        }
      },
      [onToggleSelection, hotel.id],
    );
    // Compact row in Manage mode — title-only with drag handle + checkbox.
    // Easier to drag/select on long lists; selection border owns its own border-box, so no corner clipping.
    if (isManageMode) {
      return (
        <Flex
          ref={draggableNodeRef}
          // Row owns the gesture: role="checkbox" + aria-checked makes the whole row the
          // announced control for screen readers; tabIndex={0} + onKeyDown (Space/Enter)
          // makes selection keyboard-accessible. The visual CheckboxV2 inside is aria-hidden
          // and pointerEvents="none" so it never double-fires or duplicates announcements.
          role="checkbox"
          aria-checked={isSelected}
          aria-label={hotel.displayName ?? undefined}
          tabIndex={0}
          alignItems="center"
          gap={3}
          paddingX={4}
          paddingY={3}
          backgroundColor="white"
          border="2px solid"
          borderColor={isSelected ? 'newUIBrandPrimary' : 'mono3'}
          borderRadius={pxToRem(TILE_BORDER_RADIUS)}
          cursor="pointer"
          onClick={handleClick}
          onKeyDown={handleRowKeyDown}
          transition="border-color 150ms ease-in-out"
        >
          <Flex
            alignItems="center"
            justifyContent="center"
            cursor={isDragDisabled ? 'not-allowed' : isDragActive ? 'grabbing' : 'grab'}
            opacity={isDragDisabled ? 0.3 : 1}
            color="mono10"
            onClick={e => e.stopPropagation()}
            {...(isDragDisabled ? {} : attributes)}
            {...(isDragDisabled ? {} : listeners)}
          >
            <DragIcon size={16} />
          </Flex>
          <Box
            flex={1}
            minWidth={0}
          >
            <TextEllipsisWrapper
              lineClamp={1}
              typographyVariant="body2"
              color="mono14"
            >
              {hotel.displayName}
            </TextEllipsisWrapper>
          </Box>
          <Box
            aria-hidden
            display="flex"
            alignItems="center"
            pointerEvents="none"
          >
            <CheckboxV2
              isChecked={isSelected}
              isReadOnly
            />
          </Box>
        </Flex>
      );
    }

    return (
      <Box
        position="relative"
        role="group"
        ref={draggableNodeRef}
      >
        {/* Drag Handle — outside SwipeAction to avoid overflow:hidden clipping. Shown on hover (desktop) or always (mobile). */}
        <Flex
          position="absolute"
          left={pxToRem(isMobile ? -22 : -28)}
          top="50%"
          transform="translateY(-50%)"
          height="100%"
          paddingY={6}
          opacity={(isDragActive || isMobile) && !isDragDisabled ? 1 : 0}
          _groupHover={{ opacity: isDragDisabled ? 0 : 1 }}
          transition="opacity 150ms ease-in-out"
          cursor={isDragActive ? 'grabbing' : 'grab'}
          alignItems="center"
          justifyContent="center"
          {...attributes}
          {...listeners}
        >
          <DragIcon size={16} />
        </Flex>

        <SwipeAction
          ref={swipeRef}
          disabled={isDragActive || isTourActive}
          stayOpen={false}
          borderRadius={pxToRem(TILE_BORDER_RADIUS)}
        >
          <SwipeAction.Content>
            <HotelTile
              onClick={handleClick}
              containerWidth={containerWidth}
              backgroundColor="white"
              border="1px solid"
              borderColor="mono3"
              padding={0}
              minHeight={pxToRem(TILE_THUMBNAIL_HEIGHT)}
              alignItems="stretch"
              gap={4}
            >
              {/* Thumbnail */}
              <HotelTile.Thumbnail
                src={thumbnailUrl}
                alt={hotel.displayName || ''}
              />

              {/* Content */}
              <Flex
                flex={1}
                flexDirection="column"
                justifyContent="space-between"
                alignSelf="stretch"
                minWidth={0}
                paddingY={3}
                paddingRight={3}
              >
                {/* Metadata */}
                <Flex
                  alignItems="flex-start"
                  flexDirection="column"
                  gap={pxToRem(6)}
                >
                  {(isNewlyAdded || isFavorite || isHostStayingHere || hasRoomBlock) && (
                    <Flex
                      alignItems="flex-start"
                      gap={pxToRem(6)}
                      flexWrap="wrap"
                      rowGap={2}
                    >
                      {isNewlyAdded && (
                        <TextV2
                          typographyVariant="label1"
                          color="newUIBrandPrimary"
                          textTransform="uppercase"
                          fontWeight={600}
                        >
                          {tq`Newly Added`}
                        </TextV2>
                      )}
                      <HotelTile.BadgesFromFlags
                        isFavorite={isFavorite}
                        isHostStayingHere={isHostStayingHere}
                        hasRoomBlock={hasRoomBlock}
                        roomCount={hotel.numberOfRoomsReserved}
                      />
                    </Flex>
                  )}

                  {/* Hotel Name */}
                  <TextEllipsisWrapper
                    lineClamp={2}
                    typographyVariant="hed1"
                    color="mono14"
                  >
                    {hotel.displayName}
                  </TextEllipsisWrapper>

                  {/* Metadata */}
                  {(hasHotel || hasRoomBlock) && (
                    <HotelTile.Metadata
                      rating={{ score: hotel.userRating?.ratingValue, starCount }}
                      distance={{ miles: hotel.distance, distances: hotel.distances }}
                    />
                  )}

                  {/* Custom Info */}
                  {isCustomProperty && <HotelTile.CustomInfo lines={[hotel.address ?? '']} />}
                </Flex>

                {/* Pricing */}
                <Flex
                  gap={1}
                  justifyContent="space-between"
                  alignItems={shouldAlignBottom ? 'flex-end' : 'flex-start'}
                >
                  <Flex
                    flexDirection="column"
                    gap={2}
                    flex={1}
                    minWidth={0}
                  >
                    {(hasHotel || hasRoomBlock) && (
                      <>
                        {/* Bottom Tag */}
                        {hasRoomBlock && formattedCutoffDate && <HotelTile.BottomTag tag={{ label: tq`Book by: ${formattedCutoffDate}` }} />}

                        {/* Sold Out Tag */}
                        {!hasRoomBlock && soldOutUrl && (
                          <HotelTile.BottomTag
                            tag={{
                              label: soldOutMessage.isRatesNotReleased ? tq`Room rates not released yet` : tq`Rooms not currently available`,
                              variant: 'warning',
                              tooltip: soldOutMessage.tooltip,
                            }}
                          />
                        )}
                      </>
                    )}
                  </Flex>

                  {/* Pricing */}
                  {!isCustomProperty && (
                    <HotelTile.Pricing
                      loading={pricingLoading && !skipPricing}
                      price={{ current: displayPrice, original: displayStrikeout, percentOff: displayPercentOff }}
                    />
                  )}

                  {/* Custom Info */}
                  {isCustomProperty && <HotelTile.CustomInfo lines={[hotel.websiteUrl || hotel.accommodationRoomBlock?.bookingUrl || '']} />}
                </Flex>
              </Flex>

              {/* Trash icon — non-touch devices only and not in Manage mode; touch devices use swipe-to-delete */}
              {!isTouchDevice && !isManageMode && (
                <Flex
                  position="absolute"
                  right={pxToRem(8)}
                  top="50%"
                  transform="translateY(-50%)"
                  width={pxToRem(24)}
                  height={pxToRem(24)}
                  alignItems="center"
                  justifyContent="center"
                  backgroundColor="white"
                  borderRadius={pxToRem(4)}
                >
                  <ButtonV2
                    variant="ghost"
                    intent="neutral"
                    size="sm"
                    onClick={handleRemoveClick}
                    aria-label={tq`Remove`}
                    display="none"
                    _groupHover={{ display: 'flex' }}
                    padding={0}
                    minWidth="auto"
                    height="auto"
                    color="mono10"
                    _hover={{ color: 'mono14', backgroundColor: 'transparent' }}
                  >
                    <Trash size={16} />
                  </ButtonV2>
                </Flex>
              )}
            </HotelTile>
          </SwipeAction.Content>
          <SwipeAction.Action
            intent="neutral"
            icon={<Edit size={16} />}
            onClick={handleClick}
            aria-label={tq`Edit`}
          >
            {tq`Edit`}
          </SwipeAction.Action>
          <SwipeAction.Action
            intent="destructive"
            icon={<Trash size={16} />}
            onClick={() => onRemove(hotel)}
            aria-label={tq`Remove`}
          >
            {tq`Remove`}
          </SwipeAction.Action>
        </SwipeAction>
      </Box>
    );
  },
);

SortableHotelTile.displayName = 'SortableHotelTile';
